import {z} from "zod";

import {getDateTime} from "@/src/modules/core/lib";

import {propertySchema, roleSchema} from "../../users/schemas";
import {ADJUSTMENT_ACTIONS, ADJUSTMENT_ACTIONS_ENUM} from "../lib";
import {shiftsService} from "../services";
import {shiftValidator} from "../services/shift-validator";
import {templateValidator} from "../services/template-validator";
import {ShiftType} from "../types";

export const shiftTypeItemSchema = z.object({
  label: z.string(),
  value: z.enum([ShiftType.NORMAL, ShiftType.PATROL, ShiftType.DISPATCH]),
});

const shiftTypeSchema = shiftTypeItemSchema.nullable();

export const templateSchema = z
  .object({
    template_id: z.number().optional(),
    type: shiftTypeSchema,
    template_name: z.string(),
    properties: z.array(z.object({id: z.number(), name: z.string()})),
    from_time: z.string(),
    to_time: z.string(),
    breaks: z.array(
      z.object({
        template_break_id: z.number().optional(),
        temp_id: z.string().optional(),
        break_type: z.enum(["rest", "lunch"]),
        start_time: z.string(),
        is_paid: z.coerce.number(),
        duration_minutes: z.coerce.number().min(1, {message: "Duration must be at least 1 minute"}),
      }),
    ),
  })
  .superRefine((data, ctx) => {
    if (!data.template_name) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["template_name"],
      });
    }

    if (!data.properties.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["properties"],
      });
    }

    if (!data.from_time) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["from_time"],
      });
    }

    if (!data.to_time) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["to_time"],
      });
    }

    const areRequiredBreaksPresent = templateValidator.validateTemplateRequiredBreaks(data);
    if (!areRequiredBreaksPresent) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required breaks are not present",
        path: ["breaks"],
      });
    }
  });

const NOTES_MAX_LENGTH = 300;

const userSchema = z
  .object({
    user_id: z.number(),
    first_name: z.string(),
    last_name: z.string(),
    role: roleSchema.optional(),
    dispatch_center: z
      .object({
        id: z.number(),
        name: z.string(),
        type: z.enum(["internal", "third_party"]).optional(),
        status: z.union([z.literal(0), z.literal(1)]).optional(),
        property: z
          .array(z.object({id: z.number(), name: z.string()}))
          .optional(),
      })
      .nullable()
      .optional(),
  })
  .nullable();

export const shiftSchema = z
  .object({
    shift_id: z.number().optional(),
    properties: z.array(z.object({id: z.number(), name: z.string()})),
    patrol_route: z.object({id: z.number(), name: z.string()}).nullable().default(null),
    user: userSchema,
    role_id: z.number().optional().nullable(),
    type: shiftTypeSchema,
    from_date: z.date(),
    from_time: z.string(),
    days: z
      .array(z.enum(["1", "2", "3", "4", "5", "6", "7"]))
      .optional()
      .default([]),
    to_date: z.date(),
    to_time: z.string(),
    notes: z.string().optional(),
    breaks: z.array(
      z.object({
        shift_break_id: z.number().optional(),
        temp_id: z.string().optional(),
        break_type: z.enum(["rest", "lunch"]),
        start_date: z.date(),
        start_time: z.string(),
        is_paid: z.coerce.number(),
        duration_minutes: z.coerce.number().min(1, {message: "Duration must be at least 1 minute"}),
      }),
    ),
    break_suggestion: z
      .object({
        exempt: z.boolean(),
        required: z.object({
          rest_breaks: z.number(),
          lunch_breaks: z.number(),
          lunch_breaks_rejectable: z.number(),
        }),
        entitlement_notes: z.string().nullable(),
        warnings: z.array(z.string()),
      })
      .nullable()
      .optional(),
    notify_officer: z.boolean(),
  })
  .superRefine((data, ctx) => {
    const allowedTypes = shiftsService.getShiftTypesAvailableForMe().map((t) => t.value);

    if (!data.type) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["type"],
      });
    } else if (!allowedTypes.includes(data.type.value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Invalid type for your role",
        path: ["type"],
      });
    } else if (
      data.user?.role?.name &&
      data.user.user_id > 0 &&
      data.user.first_name !== "Unassigned" &&
      !shiftsService
        .getRolesAvailableForType(data.type.value)
        .includes(data.user.role.name.trim().toLowerCase() as ReturnType<typeof shiftsService.getRolesAvailableForType>[number])
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Invalid type for selected officer",
        path: ["type"],
      });
    }

    if (!data.user) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["user"],
      });
    }

    const isPatrol = data.type?.value === ShiftType.PATROL;
    if (isPatrol) {
      // Require a route on create; optional on edit so legacy route-less shifts stay editable.
      const isCreate = !data.shift_id;
      if (isCreate && !data.patrol_route) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "This field is required",
          path: ["patrol_route"],
        });
      }
    } else if (!data.properties.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["properties"],
      });
    }

    if (!data.from_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["from_date"],
      });
    }

    if (!data.from_time) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["from_time"],
      });
    }

    if (!data.to_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["to_date"],
      });
    }

    if (!data.to_time) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["to_time"],
      });
    }

    if (data.from_date && data.to_date && data.from_time && data.to_time) {
      const fromDateTime = getDateTime(data.from_date, data.from_time);
      const toDateTime = getDateTime(data.to_date, data.to_time);

      if (toDateTime.isBefore(fromDateTime)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "To date must be after from date",
          path: ["to_date"],
        });
      }

      if (data.breaks.length > 0) {
        const isValid = shiftValidator.validateBreaksDates(
          data.from_date,
          data.from_time,
          data.to_date,
          data.to_time,
          data.breaks,
        );
        if (!isValid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "Break date must be between from date and to date and the duration cannot exceed the shift duration or overlap with other breaks",
            path: ["breaks"],
          });
        }
      }

      if (data.break_suggestion?.exempt) {
        return;
      }

      const required = data.break_suggestion?.required;
      const areRequiredBreaksPresent = required
        ? shiftValidator.validateShiftRequiredBreaks(data, required)
        : shiftValidator.validateShiftRequiredBreaks(data);

      if (required && !areRequiredBreaksPresent) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required breaks are not present",
          path: ["breaks"],
        });
      }
    }

    if (data.notes && data.notes.length > NOTES_MAX_LENGTH) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Must be less than ${NOTES_MAX_LENGTH} characters`,
        path: ["notes"],
      });
    }
  });

export const emergencyShiftSchema = z
  .object({
    type: shiftTypeItemSchema.nullable(),
    properties: z.array(propertySchema),
    patrol_route: z.object({id: z.number(), name: z.string()}).nullable().default(null),
    from_date: z.date(),
    from_time: z.string(),
    to_date: z.date(),
    to_time: z.string(),
    reason: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const roleHasPropertySelectorInEmergencyShift =
      shiftsService.getRoleHasPropertySelectorInEmergencyShift();

    const allowedTypes = shiftsService.getShiftTypesAvailableForMe().map((t) => t.value);
    if (!data.type) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["type"],
      });
    } else if (!allowedTypes.includes(data.type.value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Invalid type for your role",
        path: ["type"],
      });
    }

    const isPatrol = data.type?.value === ShiftType.PATROL;
    if (roleHasPropertySelectorInEmergencyShift) {
      if (isPatrol) {
        if (!data.patrol_route) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "This field is required",
            path: ["patrol_route"],
          });
        }
      } else if (data.properties.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "This field is required",
          path: ["properties"],
        });
      }
    }

    if (!data.from_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["from_date"],
      });
    }

    if (!data.to_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["to_date"],
      });
    }

    if (!data.from_time) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["from_time"],
      });
    }

    if (!data.to_time) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["to_time"],
      });
    }

    if (data.from_date && data.from_time && data.to_date && data.to_time) {
      const fromDateTime = getDateTime(data.from_date, data.from_time);
      const toDateTime = getDateTime(data.to_date, data.to_time);

      if (data.from_date && data.to_date && toDateTime.isBefore(fromDateTime)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "To date must be after from date",
          path: ["to_date"],
        });
      }
    }

    if (!data.reason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["reason"],
      });
    }
  });

export const bulkCopySchema = z
  .object({
    source_from: z.date().or(z.null()),
    source_to: z.date().or(z.undefined()),
    target_from: z.date().or(z.null()),
    target_to: z.date().or(z.undefined()),
  })
  .superRefine((data, ctx) => {
    if (!data.source_from) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["source_from"],
      });
    }
    if (!data.source_to) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["source_to"],
      });
    }
    if (!data.target_from) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["target_from"],
      });
    }
    if (!data.target_to) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["target_to"],
      });
    }
  });

export const adminActionSchema = z
  .object({
    action_type: z.enum(ADJUSTMENT_ACTIONS_ENUM),
    from_date: z.date().or(z.undefined()).nullable(),
    from_time: z.string().or(z.undefined()).nullable(),
    to_date: z.date().or(z.undefined()).nullable(),
    to_time: z.string().or(z.undefined()).nullable(),
    reason: z.string().or(z.undefined()).nullable(),
    notes: z.string().or(z.undefined()).nullable(),
    billing_impact: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    const type = ADJUSTMENT_ACTIONS.find((ac) => ac.id === data.action_type)!;

    if (type.requiredNewStart && !data.from_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["from_date"],
      });
    }

    if (type.requiredNewStart && !data.from_time) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["from_time"],
      });
    }

    if (type.requiredNewEnd && !data.to_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["to_date"],
      });
    }

    if (type.requiredNewEnd && !data.to_time) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["to_time"],
      });
    }

    if (!data.reason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["reason"],
      });
    }
  });
